import {useCallback} from 'react';
import {isEmpty} from 'lodash';
import {useTranslation} from 'react-i18next';

import FieldWrapper from '@/components/form/field-wrapper';
import {useDocumentCreationActions} from '@/components/new-document-creation/dialog/context/document-create-actions.hook';
import {useGetInvestorByIdQuery} from '@/components/new-document-creation/hooks/use-get-investor-by-id-query';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {ListItem} from '../list-item';
import {InvestorAccountsListItem} from './investor-accounts-list-item';
import {InvestorAccountsListTransientState} from './investor-accounts-list-transient-state';

type Props = {
  investorNo?: string;
};

export const InvestorAccountsList = ({investorNo}: Props) => {
  const {t} = useTranslation();
  const {onCreateForNewAccount} = useDocumentCreationActions();

  const {
    data: investor,
    isPending: investorIsPending,
    isRefetching: investorIsRefetching,
    isError: investorIsError,
    refetch: investorRefetch,
  } = useGetInvestorByIdQuery(investorNo);
  const accounts = investor?.accounts ?? [];

  const isLoading = investorIsPending || investorIsRefetching;

  const onRefetch = useCallback(() => {
    if (!isLoading) {
      investorRefetch();
    }
  }, [isLoading, investorRefetch]);

  if (isLoading) {
    return (
      <InvestorAccountsListTransientState
        isLoading={isLoading}
        data-testid="accounts-loading"
        retryTrackingKey={WEB_CLICK_TRACKING_KEY.DISABLED}
      />
    );
  }

  if (investorIsError) {
    return (
      <InvestorAccountsListTransientState
        data-testid="accounts-error"
        label={t('components.new-document-creation.dialog.content.step-account.accounts-error-message')}
        retryLabel={t('components.new-document-creation.dialog.content.step-account.accounts-error-retry')}
        retryTrackingKey={WEB_CLICK_TRACKING_KEY.RETRY_ACCOUNTS_ERROR}
        onRetry={onRefetch}
      />
    );
  }

  return (
    <div className="w-full space-y-3" data-testid="accounts-list">
      {isEmpty(accounts) ? (
        <InvestorAccountsListTransientState
          data-testid="accounts-empty"
          label={t('components.new-document-creation.dialog.content.step-account.accounts-empty-message')}
        />
      ) : (
        <FieldWrapper
          identifier="existing-accounts"
          label={t('components.new-document-creation.dialog.content.step-account.existing-accounts.label')}
          className="gap-2"
          showAssistiveText={false}
        >
          <div className="flex w-full grow flex-col gap-2">
            {accounts.map(account => (
              <InvestorAccountsListItem
                key={`select-account-${account.accountNo}`}
                account={account}
                investor={investor}
              />
            ))}
          </div>
        </FieldWrapper>
      )}

      <FieldWrapper
        identifier="other-options"
        label={t('components.new-document-creation.dialog.content.step-account.other-options.label')}
        showAssistiveText={false}
      >
        <ListItem
          key="new-account"
          data-testid="select-new-account"
          onClick={() => onCreateForNewAccount(investor)}
          className="bg-greige"
          trackingKey={WEB_CLICK_TRACKING_KEY.CREATE_NEW_ACCOUNT}
        >
          {t('components.new-document-creation.dialog.content.step-account.other-options.new-account.label')}
        </ListItem>
      </FieldWrapper>
    </div>
  );
};
